import type { Organization } from '@contexts/organizations-provider/api/types/organization.types';

import { fetchOrganizations } from '@contexts/organizations-provider/api/fetch-organizations/fetchOrganizations';
import { useHasPermission } from '@contexts/permissions-provider';
import { DocumentsContent } from '@features/documents/components/documents-content/DocumentsContent';
import { DocumentsHeader } from '@features/documents/components/documents-header/DocumentsHeader';
import { DocumentsSearchView } from '@features/documents/components/documents-search-view/DocumentsSearchView';
import { SelectCustomerIllustration } from '@features/documents/components/illustrations/SelectCustomerIllustration';
import { useDocumentsFilter } from '@features/documents/contexts/documents-filter/useDocumentsFilter';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import styles from './CompaniesDocumentsPage.module.css';

const CompaniesDocumentsPage = React.memo((): React.ReactElement => {
  const { filters, setFilters } = useDocumentsFilter();
  const canDownload = useHasPermission('CustomerTrainingDocuments_Open_All');
  const [isSearchActive, setIsSearchActive] = React.useState(false);

  const { data: organizations = [] } = useQuery<Organization[]>({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
    staleTime: 5 * 60 * 1000,
  });

  const selectedOrgName = React.useMemo((): string => {
    const org = organizations.find(
      o => o.id === filters.selectedOrganizationId
    );
    return org?.name ?? '';
  }, [organizations, filters.selectedOrganizationId]);

  const handleOrgChange = React.useCallback(
    (orgId: string): void => {
      setFilters({ selectedOrganizationId: orgId });
    },
    [setFilters]
  );

  const handleSearchActivate = React.useCallback((): void => {
    setIsSearchActive(true);
  }, []);

  const handleSearchBack = React.useCallback((): void => {
    setIsSearchActive(false);
  }, []);

  if (isSearchActive && filters.selectedOrganizationId) {
    return (
      <DocumentsSearchView
        title={`Search ${selectedOrgName}`}
        documentType="Customer"
        organizationId={filters.selectedOrganizationId}
        canDownload={canDownload}
        onBack={handleSearchBack}
      />
    );
  }

  return (
    <div
      data-testid="companies-documents-page"
      className={styles.companiesDocumentsPage}
    >
      <DocumentsHeader
        title="Customer training documents (FAA)"
        showOrgSelector
        showSearch={!!filters.selectedOrganizationId}
        onSearchActivate={handleSearchActivate}
        organizations={organizations}
        selectedOrgId={filters.selectedOrganizationId}
        onOrgChange={handleOrgChange}
      />
      {filters.selectedOrganizationId ? (
        <DocumentsContent
          documentType="Customer"
          organizationId={filters.selectedOrganizationId}
          canDownload={canDownload}
          rootLabel={selectedOrgName || 'Documents'}
        />
      ) : (
        <FeedbackContent
          title="To begin, select a customer"
          message="Use the dropdown to find a customer in order to view their documents."
          illustration={<SelectCustomerIllustration />}
          showPrimaryAction={false}
        />
      )}
    </div>
  );
});

CompaniesDocumentsPage.displayName = 'CompaniesDocumentsPage';

export { CompaniesDocumentsPage };
